import { IndicatorsResponse } from '@api/indicatorsApiHandler';
import RadarIcon from '@assets/images/icons/radar-outlined.svg';
import { DEFAULT_V2_IMAGE_PATH } from '@config/questionnaire-v2-images.config';
import { MotionImage, MotionText, showToast } from '@shared/atoms';
import { NoDataBlueTraySection, SystemButton } from '@shared/molecules';
import { ToastMessages } from 'constants/toastMessages';
import React from 'react';
import { useHistory } from 'react-router';
import styles from './index.module.scss';

interface WellbeingIndicatorsProps {
  indicators: IndicatorsResponse[] | [];
}

export const WellbeingIndicators: React.FC<WellbeingIndicatorsProps> = ({ indicators }) => {
  const history = useHistory();

  const getScoreClass = (status: string, positive: boolean) => {
    if (status === 'low') return !positive ? 'highScore' : 'lowScore';
    if (status === 'moderate') return 'moderateScore';
    if (status === 'high') return !positive ? 'lowScore' : 'highScore';

    return 'moderateScore'; // default
  };

  const renderIndicatorRow = (ind: IndicatorsResponse[]) => {
    // Group indicators in rows of 3
    const rows = [];
    for (let i = 0; i < ind.length; i += 3) {
      rows.push(ind.slice(i, i + 3));
    }

    return rows.map((row, rowIndex) => (
      <div key={rowIndex} className={styles.indicatorRow}>
        {row.map((indicator, index) => {
          const validatedCurrentScore =
            indicator?.latestIndicatorScore?.score && indicator?.latestIndicatorScore?.score !== -1;

          const handleTakeAssessment = () => {
            if (indicator?.specialisedQuestionnaireId) {
              history.push(
                `/assessment?questionnaireId=${indicator?.specialisedQuestionnaireId}&indicatorId=${indicator?.id}`,
              );
            } else {
              showToast(ToastMessages.common.SOMETHING_WENT_MISSING_TRY_AGAIN, undefined, 'info');
            }
          };

          return (
            <div key={index} className={styles.indicatorCard}>
              <div
                className={styles.indicatorHeader}
                onClick={() => {
                  if (validatedCurrentScore) {
                    history.push(`/assessment-scores?indicatorId=${indicator?.id}`);
                  } else {
                    handleTakeAssessment();
                  }
                }}
                style={{
                  cursor: 'pointer',
                }}
              >
                <div className={styles.leftTopSection}>
                  {indicator?.iconName && (
                    <div className={styles.iconContainer}>
                      <MotionImage
                        className={styles.icon}
                        src={`${DEFAULT_V2_IMAGE_PATH}/${indicator?.iconName}`}
                        alt=""
                        aria-hidden="true"
                        style={{
                          filter:
                            'brightness(0) saturate(100%) invert(33%) sepia(58%) saturate(1347%) hue-rotate(235deg) brightness(95%) contrast(101%)',
                        }}
                      />
                    </div>
                  )}

                  <MotionText className={styles.indicatorName} as="h4" text={indicator?.name} />
                </div>

                {validatedCurrentScore ? (
                  <div
                    className={styles.leftContainer}
                    onClick={() => {
                      if (indicator?.hasSpecialisedAssessment) {
                        history.push(`/assessment-scores?indicatorId=${indicator?.id}`);
                      }
                    }}
                    style={{
                      cursor: validatedCurrentScore ? 'pointer' : 'default',
                    }}
                  >
                    <MotionText
                      className={`${styles.indicatorLevel} ${
                        styles[getScoreClass(indicator.status, indicator?.isPositive)]
                      }`}
                      as="text"
                      text={indicator?.status || ''}
                    />

                    <div
                      className={`${styles.scoreDot} ${
                        styles[getScoreClass(indicator.status, indicator?.isPositive)]
                      }`}
                    />
                  </div>
                ) : (
                  <div className={styles.leftContainer}>
                    <div className={styles.dotContainer}>
                      <div className={styles.dot} />
                      <div className={styles.dot} />
                      <div className={styles.dot} />
                    </div>
                  </div>
                )}
              </div>

              {validatedCurrentScore ? (
                <div
                  className={styles.sliderContainer}
                  onClick={() => {
                    history.push(`/assessment-scores?indicatorId=${indicator?.id}`);
                  }}
                  style={{
                    cursor: 'pointer',
                  }}
                >
                  <div
                    className={styles.slider}
                    role="progressbar"
                    aria-valuenow={indicator?.latestIndicatorScore?.score || 0}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    style={{
                      background: `linear-gradient(90deg, ${
                        indicator?.isPositive ? '#ff0200' : '#04bc00'
                      } 0%, #ffaf83 50%, ${indicator?.isPositive ? '#04bc00' : '#ff0200'} 100%)`,
                    }}
                  >
                    {/* <div
                        className={`${styles.sliderFill} ${styles[getScoreClass(indicator.value)]}`}
                        style={{ width: `${indicator.value}%` }}
                      /> */}
                    <div
                      className={`${styles.sliderThumb} ${
                        styles[getScoreClass(indicator.status, indicator?.isPositive)]
                      }`}
                      style={{ left: `${indicator?.latestIndicatorScore?.score || 0}%` }}
                    />
                  </div>
                  <div className={styles.sliderLabels}>
                    <MotionText
                      as="span"
                      text={indicator?.isPositive ? 'Time to act' : 'Doing well'}
                    />
                    <MotionText as="span" text="" />
                    <MotionText
                      as="span"
                      text={indicator?.isPositive ? 'Doing well' : 'Time to act'}
                    />
                  </div>
                </div>
              ) : (
                <div
                  className={styles.sliderContainer}
                  onClick={handleTakeAssessment}
                  style={{ cursor: 'pointer' }}
                >
                  <div
                    className={styles.sliderLilac}
                    role="progressbar"
                    aria-valuenow={indicator?.latestIndicatorScore?.score || 0}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    style={{
                      background: `linear-gradient(90deg, #DACEFB 0%, #EED4EF 50%, #FFD8E4 100%)`,
                    }}
                  />
                </div>
              )}

              {validatedCurrentScore ? (
                <div
                  className={styles.viewInsightButton}
                  onClick={() => {
                    history.push(`/assessment-scores?indicatorId=${indicator?.id}`);
                  }}
                >
                  <MotionText as="text" text="View Insights" className={styles.text} />
                </div>
              ) : (
                !validatedCurrentScore && (
                  <div className={styles.viewInsightButton} onClick={handleTakeAssessment}>
                    <MotionText as="text" text="Reveal Your Insights" className={styles.text} />
                  </div>
                )
              )}

              <div className={styles.btnContainer}>
                <SystemButton
                  className={styles.assessmentButton}
                  aria-label={`Take ${indicator?.name.toLowerCase()} assessment`}
                  size="medium"
                  disabled={!indicator?.specialisedQuestionnaireId}
                  onClick={handleTakeAssessment}
                >
                  {indicator?.name ? `Take ${indicator?.name} Assessment` : 'Take Assessment'}
                </SystemButton>
              </div>
            </div>
          );
        })}
      </div>
    ));
  };

  return (
    <section className={styles.indicatorsSection} aria-labelledby="indicators-heading">
      <div className={styles.indicatorsHeader}>
        <div className={styles.headerContainer}>
          <MotionImage src={RadarIcon} alt="" className={styles.icon} />

          <MotionText
            id="indicators-heading"
            className={styles.sectionTitle}
            as="h3"
            text="Your wellbeing indicators"
          />
        </div>

        <MotionText
          className={styles.indicatorsDescription}
          as="text"
          text="Indicators predicted from your core assessments. Take specific assessments for more
            accurate results."
        />
      </div>

      {indicators && indicators?.length > 0 ? (
        <div className={styles.indicatorsGrid}>{renderIndicatorRow(indicators)}</div>
      ) : (
        <NoDataBlueTraySection text="No indicators available." />
      )}
    </section>
  );
};
